// Deterministic kernel execution. No LLM. Given a kernel definition
// and a parameter binding, run the steps in order, collect their
// outputs in a map keyed by step id, and produce the final value the
// render spec consumes.
//
// Steps execute against the same backends the chat uses:
//   • analytics_query → runQuery (auth-shared with the chat)
//   • python          → runPython (dedicated-origin isolated realm)
//
// Parameter substitution: any `{{paramName}}` or `{{paramName.field}}`
// placeholder in analytics_query spec strings or Python code gets
// replaced with the bound value before the step runs. Strings that
// resolve to objects get JSON.stringify'd; primitives are inserted
// raw (so a number param stays a number when substituted into spec
// JSON). Nested keys are traversed via dot-paths.
//
// Returned shape: { ok, error?, outputs, renderData, renderSpec, params }

import {
    AnalyticsDenialError,
    runQuery,
    rowsToObjects,
} from "./analyticsClient.js";
import {
    runPythonInRealm as runPython,
} from "./pythonRealmRuntime.js";
import {
    isOnDemandPythonPackage,
    normalizePythonPackageRequirements,
} from "./pythonPackageRequirements.js";
import { attachDetailRows, extractRenderData } from "./kernelDetailJoin.js";
import { applyScopeToSpec } from "./kernelScope.js";
import { runSubAgent } from "./subAgent.js";
import {
    DEFINITION_SOURCES,
    INVOCATION_SOURCES,
    RUN_SURFACES,
    evaluateKernelRunTrust,
} from "./kernelResolver.js";
import {
    DEFAULT_KERNEL_RUN_TIMEOUT_MS,
    KERNEL_RUN_CANCEL_REASON,
    KERNEL_RUN_OUTCOME,
    KernelRunCancelledError,
    cancelledKernelRunResult,
    failedKernelRunResult,
    isKernelRunCancellation,
    kernelRunCancelReasonFromSignal,
    normalizeKernelRunCancelReason,
    reportedKernelRunOutcomeOf,
    reportedKernelRunReasonCategoryOf,
} from "./kernelRunOutcome.js";
import {
    releaseKernelRunRealm,
    subscribeKernelRunRealmTeardown,
    terminateKernelRunRealm,
} from "./kernelRunRealmAdapter.js";
import {
    RUN_CONSENT_REFUSAL,
    createWheelConsentHandler,
} from "./runConsent.js";

export const KERNEL_REFUSAL_CATEGORY = Object.freeze({
    DEFINITION_SHAPE: "definition-shape",
    VIEWER_SCOPE: "viewer-scope",
    VIEWER_PERMISSION: "viewer-permission",
    PROVENANCE: "provenance",
    INTERACTION: "interaction",
    RUNTIME_CAPABILITY: "runtime-capability",
});

export const KERNEL_REFUSAL_REASON = Object.freeze({
    AGENT_CLICK_REQUIRED: "agent-click-required",
    ANALYTICS_PERMISSION_DENIED: "analytics-permission-denied",
    ASYNC_HOST_BRIDGE_UNTRUSTED: "async-host-bridge-untrusted",
    PRECOMPUTED_ROWS_UNTRUSTED: "precomputed-rows-untrusted",
    PYTHON_EXECUTION_REFUSED: "python-execution-refused",
    PYTHON_PACKAGE_REQUIREMENTS_INVALID: "python-package-requirements-invalid",
    TOOL_AUTHORITY_UNTRUSTED: "tool-authority-untrusted",
    TOOL_DEFINITION_NOT_LIVE: "tool-definition-not-live",
    UNSUPPORTED_SCOPE_MODE: "unsupported-scope-mode",
    UNSUPPORTED_STEP_KIND: "unsupported-step-kind",
    SCOPE_UNRESOLVABLE: "scope-unresolvable",
});

const PYTHON_INFRASTRUCTURE_REFUSAL_REASONS = new Set([
    "realm_payload_capacity_exceeded",
    "realm_run_limit_exceeded",
]);
const PYTHON_CONTENT_REFUSAL_REASONS = new Set([
    "realm_code_limit_exceeded",
    "realm_input_limit_exceeded",
    "realm_result_limit_exceeded",
]);
const CONSENT_REFUSAL_REASONS = new Set(Object.values(RUN_CONSENT_REFUSAL));

function pythonRefusalCategory(reason, wheelAuthority) {
    if (PYTHON_INFRASTRUCTURE_REFUSAL_REASONS.has(reason)) {
        return KERNEL_REFUSAL_CATEGORY.RUNTIME_CAPABILITY;
    }
    if (PYTHON_CONTENT_REFUSAL_REASONS.has(reason)) {
        return KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE;
    }
    if (!wheelAuthority) return KERNEL_REFUSAL_CATEGORY.PROVENANCE;
    return CONSENT_REFUSAL_REASONS.has(reason)
        ? KERNEL_REFUSAL_CATEGORY.INTERACTION
        : KERNEL_REFUSAL_CATEGORY.RUNTIME_CAPABILITY;
}

function pythonRefusalMessage(reason, wheelAuthority, runtimeMessage) {
    if (PYTHON_INFRASTRUCTURE_REFUSAL_REASONS.has(reason)) {
        return runtimeMessage || "The isolated Python runtime is at capacity.";
    }
    if (PYTHON_CONTENT_REFUSAL_REASONS.has(reason)) {
        return runtimeMessage || "The isolated Python runtime refused the step content.";
    }
    if (!wheelAuthority) {
        return "Only trusted live, non-canvas runs receive on-demand Python package authority.";
    }
    return runtimeMessage || "The isolated Python runtime refused this step.";
}

export class KernelRefusalError extends Error {
    constructor(refusal) {
        super(refusal.message || "kernel run refused");
        this.name = "KernelRefusalError";
        this.code = refusal.reasonCode;
        this.category = refusal.reasonCategory;
        this.refusal = refusal;
    }
}

// System prompt for an `agent` step. It is a transform, not an assistant:
// turn the provided inputs into a result that matches the required output
// schema, with no tools and no invented data. Kept deliberately strict so
// the only non-determinism is phrasing within the schema, never shape.
const AGENT_STEP_SYSTEM_PROMPT = [
    "You are a single transform step inside an analytics kernel.",
    "You receive structured INPUT data and must return a result that conforms EXACTLY to the required output schema (you return it via the structured-output tool).",
    "Rules:",
    "- Base the output ONLY on the provided inputs. Never invent values, ids, or facts not present in the inputs.",
    "- You have NO other tools and cannot fetch anything. Reason over the inputs and return the structured result.",
    "- Keep the output minimal and faithful — it feeds a later deterministic step, so correctness of shape and grounding matters more than prose.",
].join("\n");

// Extra options for the signals context-fetch paths (both default to
// inert so existing callers are byte-compatible):
//   precomputed  — { [stepId]: rows }. The harness batch-planned the
//                  step's analytics query across a whole scan (see
//                  signals/queryPlanner) and hands the sliced rows in;
//                  the step binds them as its output and skips
//                  execution. analytics_query steps only — an entry
//                  for any other step kind is ignored (the step runs
//                  normally) so a stale id can't silently replace real
//                  computation.
//   pythonMode   — retained as an explicit refusal input. Isolated Python has
//                  no host bridge; analytics stays in parent-owned steps.
let _nextDirectKernelRunId = 1;

function wholeKernelTimeoutMs(value) {
    if (value === 0) return 0;
    const n = Number(value);
    return Number.isFinite(n) && n > 0
        ? n
        : DEFAULT_KERNEL_RUN_TIMEOUT_MS;
}

function nextDirectKernelRunId() {
    return `kernel-run:${Date.now().toString(36)}:${_nextDirectKernelRunId++}`;
}

export async function runKernel(options = {}) {
    const externalSignal = options?.signal || null;
    const timeoutMs = wholeKernelTimeoutMs(options?.timeoutMs);
    const runId = options?.runId || nextDirectKernelRunId();
    const executionId = options?.executionId || runId;
    const runStats = { analyticsQueryCount: 0 };
    let result;
    let executionSignal = externalSignal;
    let deadlineController = null;
    let timeout = null;
    let removeExternalAbort = null;
    let unsubscribeRealm = null;
    let resolveConfirmedTermination = null;
    let cancellationReason = null;
    let cancellationStarted = false;
    const confirmedTermination = timeoutMs > 0
        ? new Promise((resolve) => { resolveConfirmedTermination = resolve; })
        : null;

    const cancelExecution = (reason, { terminateRealm = true } = {}) => {
        if (!deadlineController || cancellationStarted) return false;
        cancellationStarted = true;
        const normalized = normalizeKernelRunCancelReason(
            reason,
            KERNEL_RUN_CANCEL_REASON.ABORTED,
        );
        cancellationReason = normalized;
        let teardown;
        try {
            // As with the canvas scheduler, termination must capture realm
            // siblings before abort listeners clear the controller records.
            teardown = terminateRealm
                ? terminateKernelRunRealm({
                    runId,
                    runIds: runId ? [runId] : [],
                    executionId,
                    reason: normalized,
                    metadata: { kind: "kernel-runner" },
                })
                : { terminated: true };
        } catch (error) {
            teardown = { terminated: false, error };
        }
        try {
            deadlineController.abort(new KernelRunCancelledError(normalized));
        } catch {
            deadlineController.abort();
        }
        Promise.resolve(teardown).catch(() => null).then((teardownResult) => {
            if (teardownResult?.terminated === true) {
                resolveConfirmedTermination?.(
                    cancelledKernelRunResult(normalized, { runId }),
                );
            }
        });
        return true;
    };

    if (externalSignal?.aborted) {
        result = cancelledKernelRunResult(
            kernelRunCancelReasonFromSignal(externalSignal),
            { runId },
        );
    } else {
        if (timeoutMs > 0) {
            deadlineController = new AbortController();
            executionSignal = deadlineController.signal;
            if (externalSignal) {
                const onExternalAbort = () => {
                    cancelExecution(
                        kernelRunCancelReasonFromSignal(
                            externalSignal,
                            KERNEL_RUN_CANCEL_REASON.ABORTED,
                        ),
                    );
                };
                externalSignal.addEventListener("abort", onExternalAbort, { once: true });
                removeExternalAbort = () => {
                    externalSignal.removeEventListener("abort", onExternalAbort);
                };
            }
            unsubscribeRealm = subscribeKernelRunRealmTeardown((event) => {
                if (event?.triggerRunId === runId) return;
                const ids = Array.isArray(event?.cancelledRunIds)
                    ? event.cancelledRunIds
                    : [];
                if (event?.all !== true && (!runId || !ids.includes(runId))) return;
                cancelExecution({
                    code: KERNEL_RUN_CANCEL_REASON.COLLATERAL_REALM_TEARDOWN,
                    triggerRunId: event?.triggerRunId || null,
                }, { terminateRealm: false });
            });
            timeout = setTimeout(() => {
                cancelExecution({
                    code: KERNEL_RUN_CANCEL_REASON.TIMEOUT,
                    timeoutMs,
                    message: `Run exceeded its ${Math.round(timeoutMs / 1000)}s wall-clock limit.`,
                });
            }, timeoutMs);
        }

        const execution = (async () => {
            try {
                return await executeKernel({
                    ...options,
                    signal: executionSignal,
                    runId,
                    executionId,
                    runStats,
                });
            } catch (error) {
                return isKernelRunCancellation(error, executionSignal)
                    ? cancelledKernelRunResult(
                        kernelRunCancelReasonFromSignal(executionSignal) || error,
                        { error, runId },
                    )
                    : failedKernelRunResult(error, { runId });
            }
        })();
        try {
            result = confirmedTermination
                ? await Promise.race([execution, confirmedTermination])
                : await execution;
        } finally {
            if (timeout) clearTimeout(timeout);
            removeExternalAbort?.();
            unsubscribeRealm?.();
        }
        // An abort can race a backend resolving. Cancellation wins: callers
        // must never display data from a run the viewer stopped.
        if (executionSignal?.aborted && !result?.cancelled) {
            result = cancelledKernelRunResult(
                kernelRunCancelReasonFromSignal(executionSignal)
                    || cancellationReason,
                { error: result?.error, runId },
            );
        }
    }

    try {
        result = normalizeKernelRunResult(result, runStats.analyticsQueryCount, runId);
        try {
            options?.onOutcome?.({
                runId,
                outcome: reportedKernelRunOutcomeOf(result),
                reasonCategory: reportedKernelRunReasonCategoryOf(result),
                analyticsQueryCount: result.analyticsQueryCount,
                result,
            });
        } catch { /* observer only */ }
        return result;
    } finally {
        if (options?.releaseRealmOnComplete !== false) {
            releaseKernelRunRealm({ runId, executionId });
        }
    }
}

async function executeKernel({
    // Preferred input. `kernel` remains as a compatibility input, but a bare
    // body has UNKNOWN provenance and therefore receives no authority.
    resolvedDefinition = null,
    kernel: legacyKernel = null,
    runContext = null,
    params = {},
    onStep,
    precomputed = null,
    pythonMode = null,
    onHostCall = null,
    onLaunched = null,
    signal = null,
    runId = null,
    executionId = null,
    executionPackageRequirements: providedExecutionPackageRequirements = null,
    runStats = null,
}) {
    const evaluated = evaluateKernelRunTrust(
        resolvedDefinition ?? legacyKernel,
        runContext,
    );
    const kernel = evaluated.resolvedDefinition.body;
    const normalizedRunContext = evaluated.runContext;
    const runTrust = {
        trusted: evaluated.trusted,
        definitionSource: evaluated.definitionSource,
        invocationSource: evaluated.invocationSource,
    };
    const wheelAuthority = evaluated.definitionSource === DEFINITION_SOURCES.LIVE
        && evaluated.invocationSource === INVOCATION_SOURCES.LIVE
        && normalizedRunContext.surface !== RUN_SURFACES.CANVAS;
    if (!kernel || !Array.isArray(kernel.steps)) {
        return {
            ok: false,
            outcome: "failed",
            error: new Error("invalid kernel"),
            runTrust,
        };
    }
    const resolvedParams = resolveParams(kernel, params);
    const outputs = new Map();
    // Runs that return tenant-wide data while the viewer has an active scope:
    // either an intentional global kernel or a trusted scoped-kernel fallback.
    // Surface this so the widget cannot present widened data as scoped.
    const scopeWarnings = [];
    if (kernel.scopeMode === "global"
        && hasActiveViewerRefinement(resolvedParams)) {
        scopeWarnings.push({
            stepId: null,
            reason: "This kernel runs tenant-wide and does not use the active scope.",
        });
    }

    const knownStepKinds = new Set([
        "analytics_query",
        "python",
        "mcp_tool",
        "agent",
    ]);
    const unknownStepIndex = kernel.steps.findIndex(
        (step) => !knownStepKinds.has(step?.kind),
    );
    if (unknownStepIndex >= 0) {
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            stepIndex: unknownStepIndex,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.UNSUPPORTED_STEP_KIND,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE,
                message: `Unsupported kernel step kind: ${kernel.steps[unknownStepIndex]?.kind ?? "missing"}`,
                stepId: kernel.steps[unknownStepIndex]?.id ?? null,
            },
        });
    }

    const executionPackageNames = new Set();
    for (let index = 0; index < kernel.steps.length; index += 1) {
        const step = kernel.steps[index];
        if (step?.kind !== "python") continue;
        try {
            for (const packageName of normalizePythonPackageRequirements(step.packages)) {
                executionPackageNames.add(packageName);
            }
        } catch {
            return refusalResult({
                kernel,
                params: resolvedParams,
                outputs,
                scopeWarnings,
                runTrust,
                stepIndex: index,
                refusal: {
                    reasonCode: KERNEL_REFUSAL_REASON.PYTHON_PACKAGE_REQUIREMENTS_INVALID,
                    reasonCategory: KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE,
                    message: "Python package requirements must be unique approved package names.",
                    stepId: step.id ?? null,
                },
            });
        }
    }
    const kernelPackageRequirements = [...executionPackageNames].sort();
    let executionPackageRequirements;
    try {
        executionPackageRequirements = normalizePythonPackageRequirements(
            providedExecutionPackageRequirements ?? kernelPackageRequirements,
        );
    } catch {
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.PYTHON_PACKAGE_REQUIREMENTS_INVALID,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE,
                message: "Canvas Python package requirements are invalid.",
            },
        });
    }
    if (kernelPackageRequirements.some(
        (packageName) => !executionPackageRequirements.includes(packageName),
    )) {
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.PYTHON_PACKAGE_REQUIREMENTS_INVALID,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE,
                message: "Canvas Python package scope does not cover this kernel.",
            },
        });
    }

    if (kernel.scopeMode != null
        && kernel.scopeMode !== "global"
        && kernel.scopeMode !== "scoped") {
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.UNSUPPORTED_SCOPE_MODE,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE,
                message: `Unsupported kernel scope mode: ${kernel.scopeMode}`,
            },
        });
    }

    // Actual steps, not the mutable `agentic` authoring marker, determine
    // whether an explicit user action is required.
    const agentStepIndex = kernel.steps.findIndex((step) => step.kind === "agent");
    if (agentStepIndex >= 0 && normalizedRunContext.agentStepApproved !== true) {
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            stepIndex: agentStepIndex,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.AGENT_CLICK_REQUIRED,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.INTERACTION,
                message: "This kernel contains an agent step and needs an explicit run action.",
                stepId: kernel.steps[agentStepIndex].id ?? null,
            },
        });
    }

    const toolStepIndex = kernel.steps.findIndex((step) => step.kind === "mcp_tool");
    if (toolStepIndex >= 0
        && (!evaluated.trusted || evaluated.definitionSource !== "live")) {
        const definitionNotLive = evaluated.trusted
            && evaluated.definitionSource !== "live";
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            stepIndex: toolStepIndex,
            refusal: {
                reasonCode: definitionNotLive
                    ? KERNEL_REFUSAL_REASON.TOOL_DEFINITION_NOT_LIVE
                    : KERNEL_REFUSAL_REASON.TOOL_AUTHORITY_UNTRUSTED,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.PROVENANCE,
                message: definitionNotLive
                    ? "MCP tool steps run only from a trusted live definition."
                    : "Stored or unknown kernel runs do not receive MCP tool authority.",
                stepId: kernel.steps[toolStepIndex].id ?? null,
            },
        });
    }

    // A host bridge would restore authority the sterile realm removes.
    const asyncHostBridgeRequested = pythonMode === "async"
        || typeof onHostCall === "function";
    if (asyncHostBridgeRequested) {
        const pythonStepIndex = kernel.steps.findIndex((step) => step.kind === "python");
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            stepIndex: pythonStepIndex >= 0 ? pythonStepIndex : null,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.ASYNC_HOST_BRIDGE_UNTRUSTED,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.RUNTIME_CAPABILITY,
                message: "Isolated Python does not expose a host bridge.",
                stepId: pythonStepIndex >= 0 ? kernel.steps[pythonStepIndex].id ?? null : null,
            },
        });
    }

    const precomputedStepIndex = precomputed && typeof precomputed === "object"
        ? kernel.steps.findIndex(
            (step) => step.kind === "analytics_query"
                && Object.prototype.hasOwnProperty.call(precomputed, step.id),
        )
        : -1;
    if (precomputedStepIndex >= 0 && !evaluated.trusted) {
        return refusalResult({
            kernel,
            params: resolvedParams,
            outputs,
            scopeWarnings,
            runTrust,
            stepIndex: precomputedStepIndex,
            refusal: {
                reasonCode: KERNEL_REFUSAL_REASON.PRECOMPUTED_ROWS_UNTRUSTED,
                reasonCategory: KERNEL_REFUSAL_CATEGORY.PROVENANCE,
                message: "Stored or unknown kernel runs cannot substitute caller-supplied query rows.",
                stepId: kernel.steps[precomputedStepIndex].id ?? null,
            },
        });
    }

    for (let i = 0; i < kernel.steps.length; i++) {
        signal?.throwIfAborted?.();
        const step = kernel.steps[i];
        try { onStep?.({ phase: "start", index: i, step }); } catch { /* noop */ }
        try {
            if (step.kind === "analytics_query" && precomputed
                && Object.prototype.hasOwnProperty.call(precomputed, step.id)) {
                // Declared-prefetch: rows were fetched (deduped/merged)
                // up front by the scan harness — bind and skip execution.
                const rows = Array.isArray(precomputed[step.id]) ? precomputed[step.id] : [];
                const columns = rows.length && rows[0] && typeof rows[0] === "object"
                    ? Object.keys(rows[0]) : [];
                outputs.set(step.id, { columns, rows, raw: null, precomputed: true });
                try { onStep?.({ phase: "done", index: i, step, output: { rows } }); } catch { /* noop */ }
            } else if (step.kind === "analytics_query") {
                let spec = substituteParams(step.spec, resolvedParams);
                const unbound = fieldsWithoutValue(spec);
                if (unbound.length) throw new Error(missingValueMessage(unbound));
                // Scope-dependent kernels narrow to the active dashboard
                // scope at run time (the WHERE-half is supplied by the
                // scope; the kernel owns the SHAPE-half). See kernelScope.
                // A scope-dependent kernel that can't express the active scope on
                // its PROMPT root (a kernel that joins TOKEN off PROMPT, or a
                // files/identities scope) runs UNSCOPED — wrong-but-not-empty
                // data. Surface that to the UI (not just console) so the widget
                // can flag "showing all data, not the active scope" instead of
                // presenting a widened result as scoped. (A `model` scope now
                // scopes for real via PROMPT.model — O-1805 follow-up — so it no
                // longer lands here.)
                let scopeWarning = null;
                if (kernel.scopeMode === "scoped") {
                    // The active query-language refinement (if any) folds in as
                    // more PROMPT-rooted scope, alongside resolvedParams.scope —
                    // resolving cluster/endpoint LABELS to ids first.
                    // Lazy-import: queryLangResolve pulls dimAdapters → shell.js
                    // (touches window), so only load it when a query is present
                    // rather than at module init (keeps node-env importers safe).
                    let compiledQuery = null;
                    if (resolvedParams.query) {
                        const { resolveAndCompile } = await import("./queryLangResolve.js");
                        compiledQuery = await resolveAndCompile(resolvedParams.query);
                    }
                    const scopeResult = applyScopeToSpec(
                        spec,
                        resolvedParams.scope,
                        compiledQuery,
                    );
                    if (scopeResult.scoped) {
                        spec = scopeResult.spec;
                    } else if (!evaluated.trusted) {
                        const refusal = {
                            reasonCode: scopeResult.reasonCode
                                || KERNEL_REFUSAL_REASON.SCOPE_UNRESOLVABLE,
                            reasonCategory: scopeResult.reasonCategory
                                || KERNEL_REFUSAL_CATEGORY.DEFINITION_SHAPE,
                            message: scopeResult.reason
                                || "The active viewer scope could not be applied safely.",
                            stepId: step.id ?? null,
                            scopeReasonCode: scopeResult.reasonCode ?? null,
                            scopeReasonCategory: scopeResult.reasonCategory ?? null,
                        };
                        try {
                            onStep?.({
                                phase: "refused",
                                index: i,
                                step,
                                refusal,
                            });
                        } catch { /* noop */ }
                        return refusalResult({
                            kernel,
                            params: resolvedParams,
                            outputs,
                            scopeWarnings,
                            runTrust,
                            stepIndex: i,
                            refusal,
                        });
                    } else {
                        scopeWarning = scopeResult.reason;
                        console.warn(`[kernel] ${kernel.command || kernel.id}: scope not applied to step ${step.id} — ${scopeResult.reason}; running unscoped`);
                    }
                }
                if (scopeWarning) scopeWarnings.push({ stepId: step.id, reason: scopeWarning });
                // A kernel run must re-execute against current data, never a
                // cached wire result — bypass runQuery's result cache.
                if (runStats) runStats.analyticsQueryCount += 1;
                const result = await runQuery(spec, { cache: false, signal });
                const rows = rowsToObjects(result);
                outputs.set(step.id, { columns: result.columns, rows, raw: result, scopeWarning });
                try { onStep?.({ phase: "done", index: i, step, output: { rows }, scopeWarning }); } catch { /* noop */ }
            } else if (step.kind === "python") {
                let code = typeof step.code === "string" ? substituteString(step.code, resolvedParams) : "";
                // A signal step carries byte-identical `<entry>(event) -> list[tag]`
                // source — the same blob the backend zygote loads — so the stored
                // source stays portable. The harness owns the one-line invocation
                // (it must NOT live in the shared source). See docs/specs/signals-contract.md.
                if (step.entry) code += `\n\nresult = ${step.entry}(params["event"])\n`;
                const inputs = buildPythonInputs(step.inputs, outputs);
                // Expose `inputs` and `params` as TOP-LEVEL globals
                // so the agent's natural `inputs["q1"]` /
                // `params["threshold"]` patterns work. Wrapping them
                // under a `data` parent dict (the earlier design) was
                // the source of "no rows" failures — the agent
                // followed the system prompt's "use inputs" guidance
                // and ended up reading from the wrong path.
                const ret = await runPython({
                    resolvedDefinition: evaluated.resolvedDefinition,
                    runContext: normalizedRunContext,
                    code,
                    packageRequirements: step.packages,
                    executionPackageRequirements,
                    globals: {
                        inputs,
                        params: resolvedParams,
                    },
                    // Pick up `result = …` as the step's output even
                    // if the user code ended on an assignment (so
                    // py.runPython returned None). Pre-cleared in the
                    // runtime so stale values can't leak across steps.
                    resultVariable: "result",
                    signal,
                    runId,
                    executionId,
                    // The isolated realm keeps this attribution in its
                    // parent-side in-flight record. Only a live definition
                    // invoked live outside a canvas gets a wheel-request
                    // callback, which still requires this run to carry an
                    // active user's consent broker.
                    attribution: {
                        kind: "kernel",
                        kernelId: kernel.id ?? null,
                        kernelCommand: kernel.command ?? null,
                        stepId: step.id ?? null,
                        runTrust,
                    },
                    ...(wheelAuthority
                        && step.packages?.some(isOnDemandPythonPackage)
                        ? {
                            onWheelRequest: createWheelConsentHandler(
                                normalizedRunContext,
                                { signal },
                            ),
                        }
                        : {}),
                    onLaunched,
                });
                if (!ret.ok) {
                    if (ret.outcome === KERNEL_RUN_OUTCOME.REFUSED || ret.refused) {
                        const runtimeReason =
                            typeof ret.reason === "string"
                            && /^[a-z0-9][a-z0-9_-]{0,127}$/.test(ret.reason)
                                ? ret.reason
                                : KERNEL_REFUSAL_REASON.PYTHON_EXECUTION_REFUSED;
                        const refusal = {
                            reasonCode: runtimeReason,
                            reasonCategory: pythonRefusalCategory(
                                runtimeReason,
                                wheelAuthority,
                            ),
                            message: pythonRefusalMessage(
                                runtimeReason,
                                wheelAuthority,
                                ret.error,
                            ),
                            stepId: step.id ?? null,
                        };
                        try {
                            onStep?.({
                                phase: "refused",
                                index: i,
                                step,
                                refusal,
                            });
                        } catch { /* noop */ }
                        return refusalResult({
                            kernel,
                            params: resolvedParams,
                            outputs,
                            scopeWarnings,
                            runTrust,
                            stepIndex: i,
                            refusal,
                        });
                    }
                    const error = new Error(
                        annotateUnboundNameError(ret.error || "python step failed", inputs),
                    );
                    if (ret.unavailable || ret.outcome === KERNEL_RUN_OUTCOME.UNAVAILABLE) {
                        error.kernelRunOutcome = KERNEL_RUN_OUTCOME.UNAVAILABLE;
                    }
                    throw error;
                }
                outputs.set(step.id, ret.result);
                try { onStep?.({ phase: "done", index: i, step, output: ret.result, stdout: ret.stdout, stderr: ret.stderr }); } catch { /* noop */ }
            } else if (step.kind === "mcp_tool") {
                // Saved definitions may still carry this step, but the
                // dashboard no longer connects the servers it called.
                const error = new Error(`MCP tool steps no longer run: ${step.server || "server"}__${step.tool || "tool"} cannot be called`);
                error.kernelRunOutcome = KERNEL_RUN_OUTCOME.UNAVAILABLE;
                throw error;
            } else if (step.kind === "agent") {
                // The ONE non-deterministic step kind — a tool-less in-line
                // agent that reshapes prior outputs into a STRICT structured
                // result (validated against step.outputSchema). It's a typed
                // transformer, not a free agent: no tools (so it can't touch
                // the read-only invariant), output forced to the schema. Its
                // result lands in `outputs` like any step, so it can feed a
                // python `inputs`, the renderer, or
                // another agent. The explicit-click gate is based on this
                // actual step list; `agentic` remains authoring metadata only.
                if (!step.outputSchema || typeof step.outputSchema !== "object") {
                    throw new Error("agent step needs an `outputSchema`");
                }
                const agentInputs = buildPythonInputs(step.inputs, outputs);
                const promptText = typeof step.prompt === "string" ? substituteString(step.prompt, resolvedParams) : "";
                const task = `${promptText}\n\n## Inputs (JSON)\n${JSON.stringify(agentInputs, null, 2)}`;
                const { status, result, error } = await runSubAgent({
                    task,
                    systemPrompt: AGENT_STEP_SYSTEM_PROMPT,
                    tools: [],
                    outputSchema: step.outputSchema,
                    role: step.role || "task",
                    signal,
                });
                if (status !== "done" || result == null) {
                    throw new Error(error?.message || "agent step did not return a structured result");
                }
                outputs.set(step.id, result);
                try { onStep?.({ phase: "done", index: i, step, output: result }); } catch { /* noop */ }
            } else {
                throw new Error(`unknown step kind: ${step.kind}`);
            }
        } catch (err) {
            if (err instanceof AnalyticsDenialError) {
                const refusal = {
                    reasonCode: KERNEL_REFUSAL_REASON.ANALYTICS_PERMISSION_DENIED,
                    reasonCategory: KERNEL_REFUSAL_CATEGORY.VIEWER_PERMISSION,
                    message: "This analytics step is not permitted for the current viewer.",
                    stepId: step.id ?? null,
                    denialReason: err.reason ?? null,
                };
                try {
                    onStep?.({
                        phase: "refused",
                        index: i,
                        step,
                        refusal,
                        error: err,
                    });
                } catch { /* noop */ }
                return refusalResult({
                    kernel,
                    params: resolvedParams,
                    outputs,
                    scopeWarnings,
                    runTrust,
                    stepIndex: i,
                    refusal,
                    error: err,
                });
            }
            try { onStep?.({ phase: "error", index: i, step, error: err }); } catch { /* noop */ }
            const infrastructureReason = err?.analyticsInfrastructure === true
                ? {
                    code: "analytics-execution-failed",
                    category: KERNEL_REFUSAL_CATEGORY.RUNTIME_CAPABILITY,
                    message: err instanceof Error ? err.message : String(err),
                }
                : null;
            return {
                ok: false,
                outcome: err?.kernelRunOutcome === KERNEL_RUN_OUTCOME.UNAVAILABLE
                    ? KERNEL_RUN_OUTCOME.UNAVAILABLE
                    : KERNEL_RUN_OUTCOME.FAILED,
                unavailable: err?.kernelRunOutcome === KERNEL_RUN_OUTCOME.UNAVAILABLE,
                error: err instanceof Error ? err : new Error(String(err)),
                ...(infrastructureReason ? { reason: infrastructureReason } : {}),
                failedStepIndex: i,
                outputs,
                scopeWarnings,
                params: resolvedParams,
                runTrust,
                stepSummaries: buildStepSummaries(kernel.steps.slice(0, i + 1), outputs, null),
            };
        }
    }
    const renderSourceId = kernel.renderSource
        || kernel.steps[kernel.steps.length - 1]?.id
        || null;
    const renderData = renderSourceId ? outputs.get(renderSourceId) : null;

    const renderSpec = substituteParams(kernel.render, resolvedParams);
    return {
        ok: true,
        outcome: "completed",
        outputs,
        scopeWarnings,
        renderData: attachDetailRows(extractRenderData(renderData), renderSpec, outputs),
        renderSpec,
        params: resolvedParams,
        runTrust,
        stepSummaries: buildStepSummaries(kernel.steps, outputs, renderSourceId),
    };
}



function normalizeKernelRunResult(result, analyticsQueryCount, runId) {
    const normalized = result && typeof result === "object"
        ? result
        : failedKernelRunResult(new Error("kernel run returned no result"), { runId });
    const count = Math.max(0, Number(analyticsQueryCount) || 0);
    if (normalized.ok
        && normalized.outcome === KERNEL_RUN_OUTCOME.COMPLETED
        && Array.isArray(normalized.renderData)
        && normalized.renderData.length === 0) {
        return {
            ...normalized,
            outcome: KERNEL_RUN_OUTCOME.EMPTY,
            empty: true,
            analyticsQueryCount: count,
            ...(runId ? { runId } : {}),
        };
    }
    return {
        ...normalized,
        analyticsQueryCount: count,
        ...(runId ? { runId } : {}),
    };
}

function refusalResult({
    kernel,
    params,
    outputs,
    scopeWarnings,
    runTrust,
    refusal,
    error = null,
    stepIndex = null,
}) {
    const normalizedRefusal = {
        reasonCode: refusal?.reasonCode || "kernel-run-refused",
        reasonCategory: refusal?.reasonCategory
            || KERNEL_REFUSAL_CATEGORY.RUNTIME_CAPABILITY,
        message: refusal?.message || "Kernel run refused.",
        ...refusal,
    };
    return {
        ok: false,
        outcome: "refused",
        refusal: normalizedRefusal,
        reasonCode: normalizedRefusal.reasonCode,
        reasonCategory: normalizedRefusal.reasonCategory,
        error: error instanceof Error
            ? error
            : new KernelRefusalError(normalizedRefusal),
        ...(Number.isInteger(stepIndex) ? { refusedStepIndex: stepIndex } : {}),
        outputs,
        scopeWarnings,
        params,
        runTrust,
        stepSummaries: buildStepSummaries(
            Number.isInteger(stepIndex)
                ? kernel.steps.slice(0, stepIndex + 1)
                : [],
            outputs,
            null,
        ),
    };
}

// One-line summary per step: id, kind, and a short shape descriptor
// of its output. Powers the "kernel ran, but what did each step
// produce?" diagnostic surface in the chat UI — lets the user spot
// "q1: 84 rows → py1: 0 rows" without needing to crack open the
// runner's internals.
function buildStepSummaries(steps, outputs, renderSourceId) {
    return steps.map((step) => {
        const out = outputs.get(step.id);
        return {
            id: step.id,
            kind: step.kind,
            isRenderSource: step.id === renderSourceId,
            shape: shapeOf(step, out),
        };
    });
}

function shapeOf(step, out) {
    if (out == null) return "no output";
    if (step.kind === "analytics_query") {
        const rows = Array.isArray(out?.rows) ? out.rows.length : null;
        const cols = Array.isArray(out?.columns) ? out.columns : [];
        if (rows == null) return "(query returned no rows array)";
        return `${rows} row${rows === 1 ? "" : "s"}${cols.length ? ` · cols=[${cols.slice(0, 6).join(", ")}${cols.length > 6 ? ", …" : ""}]` : ""}`;
    }
    // python step output passes through verbatim
    if (out === null) return "null";
    if (Array.isArray(out)) {
        if (!out.length) return "empty array";
        const first = out[0];
        if (typeof first === "number") return `array<number> · ${out.length}`;
        if (first && typeof first === "object") {
            const keys = Object.keys(first).slice(0, 6).join(", ");
            return `${out.length} row${out.length === 1 ? "" : "s"}${keys ? ` · cols=[${keys}]` : ""}`;
        }
        return `array · ${out.length}`;
    }
    if (typeof out === "object") {
        const keys = Object.keys(out).slice(0, 6).join(", ");
        return `object · keys=[${keys}]`;
    }
    return String(typeof out);
}

function hasActiveViewerRefinement(params) {
    const hasScopePath = Array.isArray(params?.scope) && params.scope.length > 0;
    const query = params?.query;
    const hasQueryRefinement = typeof query === "string"
        ? query.trim().length > 0
        : Boolean(query?.filter);
    return hasScopePath || hasQueryRefinement;
}

// Merge call-site overrides with declared defaults. Coerces simple
// types so {{days}} substituted from a "7" string still does the
// right thing when the analytics spec expects a number.
function resolveParams(kernel, overrides) {
    const decls = kernel.params || {};
    const out = {};
    for (const [name, decl] of Object.entries(decls)) {
        const raw = overrides && Object.prototype.hasOwnProperty.call(overrides, name)
            ? overrides[name]
            : decl.default;
        out[name] = coerce(raw, decl.type);
    }
    // Allow extra overrides that weren't declared — useful for ad-hoc
    // invocation. They won't be type-coerced; whatever the caller
    // passed flows through.
    if (overrides) {
        for (const [name, value] of Object.entries(overrides)) {
            if (!(name in out)) out[name] = value;
        }
    }
    return out;
}

function coerce(value, type) {
    if (value == null) return value;
    if (type === "number") {
        const n = Number(value);
        return Number.isFinite(n) ? n : value;
    }
    if (type === "boolean") {
        if (typeof value === "boolean") return value;
        if (value === "true") return true;
        if (value === "false") return false;
        return Boolean(value);
    }
    if (type === "string") return String(value);
    return value;
}

// Walk arbitrary JSON, substituting {{path}} placeholders in any
// string values. Objects and arrays are recursed into. When a string
// is *entirely* a placeholder, we substitute the raw value (preserves
// type — number stays number); otherwise we string-interpolate.
export function substituteParams(node, params) {
    if (node == null) return node;
    if (typeof node === "string") return substituteString(node, params);
    if (Array.isArray(node)) return node.map((v) => substituteParams(v, params));
    if (typeof node === "object") {
        const out = {};
        for (const [k, v] of Object.entries(node)) {
            // A markdown render's `template` uses the same {{...}}
            // delimiters for its OWN data-interpolation engine
            // (markdownTemplate.js). Param-substitution would eat field
            // placeholders ({{tool_name}} → "") before the renderer ever
            // ran the template, so leave it verbatim — the template
            // resolves {{...}} against the render DATA at display time,
            // not against kernel params.
            if (k === "template" && node.kind === "markdown") {
                out[k] = v;
                continue;
            }
            out[k] = substituteParams(v, params);
        }
        return out;
    }
    return node;
}

export function fieldsWithoutValue(node, out = []) {
    if (node == null || typeof node !== "object") return out;
    if (Array.isArray(node)) {
        for (const child of node) fieldsWithoutValue(child, out);
        return out;
    }
    const pred = node.pred;
    if (pred && typeof pred === "object" && pred.op !== "OPERATOR_IS_NULL" && pred.op !== "OPERATOR_IS_NOT_NULL"
        && (pred.value === null || pred.value === undefined)) {
        out.push(pred.field?.field ?? "a filter");
    }
    for (const value of Object.values(node)) fieldsWithoutValue(value, out);
    return out;
}

export function missingValueMessage(fields) {
    return `Skipped: ${fields.join(", ")} has no value here, so this query was not sent.`;
}

const PLACEHOLDER_RE = /\{\{\s*([\w.]+)\s*\}\}/g;
const WHOLE_PLACEHOLDER_RE = /^\s*\{\{\s*([\w.]+)\s*\}\}\s*$/;

function substituteString(str, params) {
    if (typeof str !== "string") return str;
    const whole = WHOLE_PLACEHOLDER_RE.exec(str);
    if (whole) {
        const val = lookup(params, whole[1]);
        return val !== undefined ? val : str;
    }
    return str.replace(PLACEHOLDER_RE, (_, path) => {
        const val = lookup(params, path);
        if (val === undefined || val === null) return "";
        if (typeof val === "string") return val;
        try { return JSON.stringify(val); } catch { return String(val); }
    });
}

function lookup(obj, path) {
    if (!path) return undefined;
    const parts = path.split(".");
    let cur = obj;
    for (const part of parts) {
        if (cur == null) return undefined;
        cur = cur[part];
    }
    return cur;
}

// Pack the named prior-step outputs into a plain object the Python
// step can read via `inputs["name"]`. analytics_query outputs are
// flattened to a list of row dicts so they're directly pd.DataFrame-
// friendly. Python step outputs pass through as-is.
function buildPythonInputs(inputs, outputs) {
    if (!inputs || typeof inputs !== "object") return {};
    const packed = {};
    for (const [name, stepId] of Object.entries(inputs)) {
        if (!outputs.has(stepId)) continue;
        packed[name] = unwrapStepOutput(outputs.get(stepId));
    }
    return packed;
}

// An analytics_query output is stored as { columns, rows, ... }; a python step
// stores its value directly. Consumers want the rows, which are already
// pd.DataFrame-friendly dicts.
function unwrapStepOutput(out) {
    return out && typeof out === "object" && Array.isArray(out.rows) ? out.rows : out;
}

// A bare `NameError` names the symbol that was missing but not what WAS in
// scope, so an author -- human or agent -- has to guess. Guessing is expensive
// here: each attempt is a full re-run. Step outputs are reachable only through
// the `inputs` dict, never as bare locals, so say that once and list the keys.
export function annotateUnboundNameError(message, inputs) {
    const text = String(message ?? "");
    const missing = /NameError: name '([^']+)' is not defined/.exec(text);
    if (!missing) return text;
    const keys = Object.keys(inputs || {});
    const available = keys.length
        ? `inputs[${keys.map((k) => JSON.stringify(k)).join("], inputs[")}]`
        : "nothing (no earlier step has run)";
    return `${text}\n\nStep outputs are only reachable through the \`inputs\` dict, `
        + `not as bare names. Available here: ${available}.`
        + (keys.includes(missing[1])
            ? ` Did you mean inputs[${JSON.stringify(missing[1])}]?`
            : "");
}

// Decide what goes to the render primitive as `data`. For
// analytics_query outputs we hand over the row list (matches what
// the kernel render specs assume — `bars`/`table`/`rows` all consume
// arrays of objects). For Python outputs, pass through verbatim.
